// src/components/gratitude/GratitudeJournal.tsx
// Daily gratitude entries with history view

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ChevronLeft, Plus, Calendar, Sparkles, Check, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase/browserClient';
import { useAuth } from '../../hooks/useAuth';
import { ToolHeader } from '../ui/ToolHeader';

interface GratitudeEntry {
  id: string;
  user_id: string;
  items: string[];
  reflection: string | null;
  date: string;
  created_at: string;
}

interface GratitudeJournalProps {
  onClose?: () => void;
  embedded?: boolean;
}

export function GratitudeJournal({ onClose, embedded = false }: GratitudeJournalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [view, setView] = useState<'today' | 'history'>('today');
  const [gratitudeItems, setGratitudeItems] = useState<string[]>(['', '', '']);
  const [reflection, setReflection] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [todayEntry, setTodayEntry] = useState<GratitudeEntry | null>(null);
  const [history, setHistory] = useState<GratitudeEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  // Load today's entry on mount
  useEffect(() => {
    if (user?.id) {
      loadTodayEntry();
    }
  }, [user?.id]);

  const loadTodayEntry = async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from('gratitude_entries')
      .select('*')
      .eq('user_id', user.id)
      .eq('date', today)
      .single();

    if (!error && data) {
      setTodayEntry(data);
      setGratitudeItems(data.items.length >= 3 ? data.items : [...data.items, '', '', ''].slice(0, 3));
      setReflection(data.reflection || '');
      setSaved(true);
    }
  };

  const loadHistory = async () => {
    if (!user?.id) return;
    
    setLoadingHistory(true);
    
    const { data, error } = await supabase
      .from('gratitude_entries')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false })
      .limit(30);

    if (!error && data) {
      setHistory(data);
    }
    
    setLoadingHistory(false);
  };

  const handleSave = async () => {
    if (!user?.id) return;
    
    const filledItems = gratitudeItems.filter(item => item.trim());
    if (filledItems.length === 0) return;

    setSaving(true);

    try {
      if (todayEntry) {
        // Update existing entry
        await supabase
          .from('gratitude_entries')
          .update({
            items: filledItems,
            reflection: reflection.trim() || null
          })
          .eq('id', todayEntry.id);
      } else {
        // Create new entry
        await supabase
          .from('gratitude_entries')
          .insert({
            user_id: user.id,
            items: filledItems,
            reflection: reflection.trim() || null,
            date: today
          });
      }

      setSaved(true);
      loadTodayEntry();
      
      // Log activity for streak
      await supabase.from('activity_log').insert({
        user_id: user.id,
        activity_type: 'gratitude',
        activity_date: today
      });

    } catch (err) {
      console.error('Error saving gratitude:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (id: string) => {
    if (!confirm('Delete this entry?')) return;
    
    await supabase
      .from('gratitude_entries')
      .delete()
      .eq('id', id);
    
    loadHistory();
    if (todayEntry?.id === id) {
      setTodayEntry(null);
      setGratitudeItems(['', '', '']);
      setReflection('');
      setSaved(false);
    }
  };

  const handleViewChange = (newView: 'today' | 'history') => {
    setView(newView);
    if (newView === 'history') {
      loadHistory();
    }
  };

  const addGratitudeSlot = () => {
    if (gratitudeItems.length < 10) {
      setGratitudeItems([...gratitudeItems, '']);
      setSaved(false);
    }
  };

  const updateGratitudeItem = (index: number, value: string) => {
    const newItems = [...gratitudeItems];
    newItems[index] = value;
    setGratitudeItems(newItems);
    setSaved(false);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const handleClose = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate('/chat');
    }
  };

  const wrapperClass = embedded
    ? "flex-1 overflow-y-auto"
    : "min-h-screen bg-gradient-to-br from-pink-100/40 via-purple-50/40 to-cyan-50/40";

  return (
    <div className={wrapperClass}>
      <ToolHeader
        title="Gratitude Journal"
        onBack={handleClose}
        backLabel="Chat"
        rightAction={
          <button
            onClick={() => handleViewChange(view === 'today' ? 'history' : 'today')}
            className={`p-2 rounded-xl transition-colors ${view === 'history' ? 'bg-white/50 text-purple-700' : 'text-purple-600 hover:bg-white/30'}`}
          >
            <Calendar className="w-5 h-5" />
          </button>
        }
      />

      <div className="max-w-2xl mx-auto px-6">
        <AnimatePresence mode="wait">
          {view === 'today' ? (
            // Today's Entry
            <motion.div
              key="today"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <div className="text-center mb-6">
                <p className="text-purple-500 text-sm mb-2">{formatDate(today)}</p>
                <h3 className="text-xl font-semibold text-purple-800">
                  What are you grateful for today?
                </h3>
              </div>

              {/* Gratitude Entries - Glass Card Container */}
              <div className="bg-white/30 backdrop-blur-md border border-white/50 rounded-2xl p-6 max-w-xl mx-auto mb-6">
                <div className="space-y-4">
                  {gratitudeItems.map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 border border-white/60 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-purple-600">{index + 1}</span>
                      </div>
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateGratitudeItem(index, e.target.value)}
                        placeholder="I'm grateful for..."
                        className="flex-1 px-4 py-3 bg-white/40 border border-white/50 rounded-xl text-purple-800 placeholder:text-purple-400 focus:outline-none focus:border-purple-300 focus:bg-white/50 transition-all"
                      />
                    </div>
                  ))}
                </div>

                {gratitudeItems.length < 10 && (
                  <button
                    onClick={addGratitudeSlot}
                    className="mt-4 flex items-center gap-2 text-purple-500 hover:text-purple-700 text-sm font-medium transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add another
                  </button>
                )}
              </div>

              {/* Reflection (optional) */}
              <div className="bg-white/30 backdrop-blur-md border border-white/50 rounded-2xl p-6 max-w-xl mx-auto mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-medium text-purple-600">Reflection (optional)</span>
                </div>
                <textarea
                  value={reflection}
                  onChange={(e) => {
                    setReflection(e.target.value);
                    setSaved(false);
                  }}
                  placeholder="Any thoughts or feelings that came up..."
                  rows={3}
                  className="w-full px-4 py-3 bg-white/40 border border-white/50 rounded-xl text-purple-800 placeholder:text-purple-400 focus:outline-none focus:border-purple-300 focus:bg-white/50 transition-all resize-none"
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleSave}
                  disabled={saving || gratitudeItems.every(item => !item.trim())}
                  className={`px-8 py-3 rounded-xl font-medium transition-all flex items-center gap-2 shadow-md ${
                    saved
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50'
                  }`}
                >
                  {saving ? (
                    <>
                      <Sparkles className="w-5 h-5 animate-pulse" />
                      Saving...
                    </>
                  ) : saved ? (
                    <>
                      <Check className="w-5 h-5" />
                      Saved for today
                    </>
                  ) : (
                    <>
                      <Heart className="w-5 h-5" />
                      Save Gratitude
                    </>
                  )}
                </button>
              </div>

              {saved && (
                <p className="text-center text-purple-500 text-sm mt-4">
                  ✨ You can update this anytime today
                </p>
              )}
            </motion.div>
          ) : (
            // History View
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-rose-900">Your Gratitude Journey</h2>
                <p className="text-rose-600 text-sm mt-1">Look back at all you've appreciated</p>
              </div>

              {loadingHistory ? (
                <div className="text-center py-12">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-rose-400 animate-pulse" />
                  <p className="text-rose-500">Loading...</p>
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12 text-rose-500">
                  <Heart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No entries yet</p>
                  <p className="text-sm mt-2">Start your gratitude practice today!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {history.map((entry) => (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl p-5 shadow-sm border border-rose-100"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-rose-600 font-medium text-sm">
                          {formatDate(entry.date)}
                        </p>
                        <button
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <ul className="space-y-2 mb-3">
                        {entry.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-700">
                            <Heart className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>

                      {entry.reflection && (
                        <div className="pt-3 border-t border-rose-100">
                          <p className="text-gray-600 text-sm italic">"{entry.reflection}"</p>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default GratitudeJournal;
